import { find } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { telegramFlowApi, shopsApi } from 'api';
import { DataGridColumnDefinition, DataGridColumnHeader } from 'components';
import { QueryKey, FilterDefinitionType } from 'enums';
import { usePartialQuery, useShopsQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { TelegramFlow, FilterDefinition, TelegramUser } from 'types';
import { formUtils } from 'utils';

import { TelegramFlowContext } from '../TelegramFlowDialog';
import { TelegramFlowTab } from '../TelegramFlowTab';
import { TelegramFlowFilters } from '../types';

type ShopsTabProps = {
  telegramUsers: TelegramUser[];
};

export const ShopsTab: React.FC<ShopsTabProps> = ({ telegramUsers }) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.telegram_flow',
  });

  const { role } = useUser();

  const queryResult = usePartialQuery(
    QueryKey.TelegramFlowShops,
    telegramFlowApi.getShopsPaginated,
  );

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
  );

  const entityColumn: DataGridColumnDefinition<TelegramFlow> = {
    header: t('fields.shop'),
    valueGetter: (item: TelegramFlow) => item.shop?.name || item.shopId,
  };

  const filtersDefinitions: FilterDefinition<TelegramFlowFilters>[] = useMemo(
    () => [
      {
        label: t('filters.shop'),
        name: 'shopId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(queryResultShops.data || []),
        getDisplayName: (shopId: string) =>
          find(queryResultShops.data, { id: shopId })?.name,
      },
      {
        label: t('filters.chat_id'),
        name: 'chatId',
        type: FilterDefinitionType.Text,
      },
    ],
    [t, queryResultShops.data],
  );

  const additionalColumns = useMemo(
    (): DataGridColumnDefinition<TelegramFlow>[] => [
      {
        header: (
          <DataGridColumnHeader
            title={t('fields.telegram_user')}
            tooltip={t('fields.telegram_user_helper')}
          />
        ),
        valueGetter: (item) => item.telegramUser?.name,
      },
    ],
    [t],
  );

  return (
    <TelegramFlowTab
      filtersDefinitions={filtersDefinitions}
      entityColumn={entityColumn}
      queryResult={queryResult}
      context={TelegramFlowContext.Shop}
      telegramUsers={telegramUsers}
      additionalColumns={additionalColumns}
    />
  );
};
